import React from "react";
import { Trans } from "@lingui/macro";
import styled from "@emotion/styled";
import { Box, Grid, Stack, Typography, useMediaQuery } from "@mui/material";
import { LocalizedLink, useLocalization } from "gatsby-theme-i18n";
import { faLinkedin } from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { navigate } from "gatsby";
import Flag from "./Flag";
import logoNegative from "../images/logo-negative.png";
import { getCountryByLanguage } from "../utils/screen";
import { availablesLanguages } from "../utils/common";

const sections = () => [
  {
    label: <Trans>Quick Links</Trans>,
    items: [
      {
        label: <Trans>About Us</Trans>,
        path: "/about",
      },
      {
        label: <Trans>Our Team</Trans>,
        path: "/about/team",
      },
      {
        label: <Trans>News</Trans>,
        path: "/blog",
      },
      {
        label: <Trans>Careers</Trans>,
        path: "/careers",
      },
      {
        label: <Trans>Contact</Trans>,
        path: "/contact",
      },
      {
        label: <Trans>Client Care</Trans>,
        path: "/client-care",
      },
    ],
  },
  {
    label: <Trans>One-Stop Shop</Trans>,
    items: [
      {
        label: <Trans>Distributors</Trans>,
        path: "/distributors",
      },
      {
        label: <Trans>Fund Houses</Trans>,
        path: "/fund-houses",
      },
    ],
  },
  {
    label: <Trans>Investors</Trans>,
    items: [
      {
        // label: <Trans>About Allfunds</Trans>,
        label: "About Allfunds",
        path: "/investors/about",
      },
      {
        // label: <Trans>Share Information</Trans>,
        label: "Share Information",
        path: "/investors/share",
      },
      {
        // label: <Trans>Financial Information and Reports</Trans>,
        label: "Financial Information and Reports",
        path: "/investors/financial/financial-reports",
      },
      {
        // label: <Trans>Shareholder Meetings</Trans>,
        label: "Shareholder Meetings",
        path: "/investors/shareholder-meetings",
      },
      {
        // label: <Trans>Governance</Trans>,
        label: "Governance",
        path: "/investors/governance/group",
      },
      {
        // label: <Trans>Contact</Trans>,
        label: "Contact",
        path: "/investors/contact",
      },
    ],
  },
];

const moreInfo = () => [
  { label: <Trans>Privacy Policy</Trans>, path: "/legal-advice/privacy-policy" },
  { label: <Trans>Legal Notice</Trans>, path: "/legal-advice/legal-notice" },
  { label: <Trans>Cookies Policy</Trans>, path: "/legal-advice/cookies-policy" },
  { label: <Trans>Reporting Channel</Trans>, path: "/legal-advice/reporting-channel" },
  // { label: <Trans>Whistleblowing Channel</Trans>, path: "/" },
];

function Footer({ small = false, light = false }) {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const { locale, config } = useLocalization();
  const availablesLangs = config.filter(
    ({ code }) => code !== locale && availablesLanguages.includes(code),
  );
  return (
    <>
      <Box component="footer">
        {!small && (
          <Box
            padding={5}
            display="flex"
            alignItems="center"
            sx={{
              minHeight: "360px",
              borderTop: (theme) => `1px solid ${theme.palette.primary.main}`,
            }}
          >
            <Grid container>
              <Grid
                item
                xs={12}
                lg={2.5}
                sx={{
                  textAlign: { xs: "start", lg: "end" },
                  "> img": {
                    width: { xs: "120px", sm: "175px" },
                  },
                }}
              >
                <img alt="" src={logoNegative} style={{ width: "175px" }} />
              </Grid>
              <StyledDivider />
              <Grid item xs spacing={5}>
                <Grid container spacing={2}>
                  {sections().map(({ label, items }) => (
                    <Grid item xs={12} sm={4}>
                      <Typography
                        variant="h4"
                        mb={2}
                        sx={{ color: (theme) => theme.palette.primary.main }}
                      >
                        {label}
                      </Typography>
                      <Stack spacing={1}>
                        {items.map((item) => (
                          <Link to={item.path || "/"}>
                            <Typography variant="comment">
                              {item.label}
                            </Typography>
                          </Link>
                        ))}
                      </Stack>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            alignitems: "center",
            minHeight: (theme) => theme.spacing(7),
            backgroundColor: (theme) =>
              (light ? theme.palette.neutral.light : theme.palette.primary.main),
          }}
          py={2}
          px={4}
        >
          <Grid
            container
            spacing={2}
            alignItems="center"
            textAlign="center"
            justifyContent="space-between"
          >
            <Grid item xs={12} md={5.5}>
              <Stack
                alignItems={isMobile ? "center" : "baseline"}
                direction={isMobile ? "column" : "row"}
                spacing={isMobile ? 1 : 2}
                sx={{
                  justifyContent: { xs: "space-evenly", md: "inherit" },
                }}
              >
                {moreInfo().map(({ label, path }) => (
                  <LocalizedLink to={path || "/"}>
                    <Typography
                      variant="comment"
                      sx={{ cursor: "pointer" }}
                      color={light ? "primary.main" : "inherit.main"}
                    >
                      {label}
                    </Typography>
                  </LocalizedLink>
                ))}
              </Stack>
            </Grid>
            {!small && (
              <Grid item xs={12} md>
                <FontAwesomeIcon
                  style={{ cursor: "pointer", width: "24px", height: "24px" }}
                  color="white"
                  icon={faLinkedin}
                  onClick={() =>
                    window.open(
                      "https://www.linkedin.com/company/allfunds-wealthtech/mycompany/",
                    )}
                />
              </Grid>
            )}
            {isMobile && (
              <Grid item xs={12}>
                <Stack
                  spacing={1}
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  sx={{ width: "100%" }}
                >
                  {availablesLangs.map((lang) => (
                    <Box
                      onClick={() =>
                        navigate(
                          window.location.pathname.replace(locale, lang.code),
                        )}
                    >
                      <Flag
                        size="small"
                        country={getCountryByLanguage(lang.code) || lang.code}
                      />
                    </Box>
                  ))}
                </Stack>
              </Grid>
            )}
            <Grid
              item
              xs={12}
              md={5.5}
              sx={{ textAlign: { xs: "center", md: "end" } }}
            >
              <Typography
                variant="comment"
                color={light ? "primary.main" : "inherit.main"}
              >
                <Trans>Copyright</Trans>
                {` © ${new Date().getFullYear()} `}
                <Trans>Allfunds Bank S.A.U., Allfunds is a registered trademark. All rights reserved</Trans>
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
}
export default Footer;

const StyledDivider = styled(Box)(
  ({ theme }) => `
  ${theme.breakpoints.down("lg")} {
      border-bottom: solid 1px ${theme.palette.neutral.main};
      margin: ${theme.spacing(2)} 0;
      width: 100%;
  }
  ${theme.breakpoints.up("lg")} {
      border-left: solid 1px ${theme.palette.neutral.main};
      margin: 0 ${theme.spacing(10)};
  }
`,
);

const Link = styled(LocalizedLink)`
  &:hover {
    text-decoration: underline;
    text-decoration-color: ${({ theme }) => theme.palette.secondary.main};
    text-decoration-thickness: 2px;
    text-underline-offset: 2px;
  }
`;
